/**
 * CreativeProfessionalTemplate.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ATS Rating: 88 / 100 — ATS Caution
 * Style: Modern accent sidebar + bold personal brand header
 * Best For: Design, marketing, content, UX roles
 * NOTE: Use PDF version; submit via portfolio link when possible
 */

import React from 'react';
import EditableField from '../components/EditableField';
import {
  normalizeTemplateData, BulletList,
  EmptyState, DS,
} from './templateDesignSystem';

function CreativeProfessionalTemplate({
  resumeData,
  accentColor = '#7C3AED',
  fontScale = 1,
  editMode = false,
  onFieldUpdate = () => { },
  onDeleteBullet = null,
  onDeleteSkill = null,
}) {
  if (!resumeData) return <EmptyState />;
  const d = normalizeTemplateData(resumeData);
  const { name, role, summaryText, skills, experience, internships, projects, educationList, certList, achieveList, extraList, languageList, contactItems, linkItems } = d; // eslint-disable-line no-unused-vars
  const scale = fontScale || 1;
  const fs = (base) => `${parseFloat(base) * scale}px`;

  const MainSection = ({ title }) => (
    <div style={{ display: 'flex', alignItems: 'center', margin: '12px 0 6px' }}>
      <h2 style={{ fontSize: fs(DS.fs.section), fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.2px', color: accentColor, margin: 0, fontFamily: 'inherit' }}>{title}</h2>
      <div style={{ flex: 1, height: 1.5, background: accentColor, marginLeft: 10, opacity: 0.2 }} />
    </div>
  );

  const SideSection = ({ title }) => (
    <div style={{ marginBottom: 4, marginTop: 12 }}>
      <h2 style={{ fontSize: fs(DS.fs.section), fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color: '#fff', margin: '0 0 6px', fontFamily: 'inherit', opacity: 0.85 }}>{title}</h2>
      <div style={{ height: 1, background: 'rgba(255,255,255,0.2)', marginBottom: 6 }} />
    </div>
  );

  const all = [...contactItems, ...linkItems.map(l => l.label)];

  return (
    <div id="resume" style={{ background: '#fff', color: '#111', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: fs(DS.fs.body), lineHeight: 1.5, display: 'flex', minHeight: '100%', boxSizing: 'border-box' }}>

      {/* ─── Left Sidebar ─── */}
      <aside
        data-ats="sidebar"
        style={{
          width: '32%', flexShrink: 0,
          background: accentColor,
          padding: '22px 14px',
          display: 'flex', flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        {/* Name + Role in sidebar */}
        <div style={{ marginBottom: 10 }}>
          <EditableField editMode={editMode} value={name || ''} onSave={v => onFieldUpdate('name', v)} tag="h1"
            style={{ fontSize: fs('18px'), fontWeight: 900, color: '#fff', margin: 0, lineHeight: 1.2, fontFamily: 'inherit' }} placeholder="Your Name" />
          {(role || editMode) && (
            <EditableField editMode={editMode} value={role || ''} onSave={v => onFieldUpdate('role', v)} tag="p"
              style={{ fontSize: fs('9.5px'), fontWeight: 600, color: 'rgba(255,255,255,0.75)', margin: '4px 0 0', letterSpacing: '0.5px', textTransform: 'uppercase', fontFamily: 'inherit' }} placeholder="UX Designer / Art Director" />
          )}
        </div>

        <div style={{ height: 1.5, background: 'rgba(255,255,255,0.3)', marginBottom: 10 }} />

        {/* Contact */}
        <section data-ats="contact" style={{ marginBottom: 8 }}>
          <SideSection title="Contact" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {all.map((item, i) => (
              <span key={i} style={{ fontSize: fs(DS.fs.small), color: 'rgba(255,255,255,0.85)', wordBreak: 'break-all', lineHeight: 1.4 }}>{item}</span>
            ))}
          </div>
        </section>

        {/* Skills sidebar */}
        {skills.length > 0 && (
          <section data-ats="skills" style={{ marginBottom: 8 }}>
            <SideSection title="Skills" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {skills.map((s, i) => (
                <span key={i} style={{
                  fontSize: fs(DS.fs.small), color: 'rgba(255,255,255,0.88)',
                  paddingLeft: 8, position: 'relative', lineHeight: 1.4,
                }}>
                  <span style={{ position: 'absolute', left: 0, color: 'rgba(255,255,255,0.5)' }}>▪</span>
                  {s}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Education sidebar */}
        {educationList.length > 0 && (
          <section data-ats="education" style={{ marginBottom: 8 }}>
            <SideSection title="Education" />
            {educationList.map((edu, idx) => (
              <div key={idx} style={{ marginBottom: 8 }}>
                <p style={{ fontSize: fs(DS.fs.body), color: '#fff', fontWeight: 700, margin: 0, lineHeight: 1.3 }}>{edu?.degree}</p>
                <p style={{ fontSize: fs(DS.fs.small), color: 'rgba(255,255,255,0.7)', margin: '2px 0' }}>{edu?.college}</p>
                {edu?.cgpa && <p style={{ fontSize: fs(DS.fs.small), color: 'rgba(255,255,255,0.6)', margin: 0 }}>CGPA: {edu.cgpa}</p>}
                <p style={{ fontSize: fs(DS.fs.small), color: 'rgba(255,255,255,0.55)', margin: 0, fontStyle: 'italic' }}>{edu?.year}</p>
              </div>
            ))}
          </section>
        )}

        {/* Certifications */}
        {certList.length > 0 && (
          <section data-ats="certifications" style={{ marginBottom: 8 }}>
            <SideSection title="Certifications" />
            {certList.map((cert, i) => (
              <p key={i} style={{ fontSize: fs(DS.fs.small), color: 'rgba(255,255,255,0.82)', margin: '0 0 3px', paddingLeft: 8, position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: 'rgba(255,255,255,0.45)' }}>▪</span>
                {cert}
              </p>
            ))}
          </section>
        )}

        {languageList.length > 0 && (
          <section data-ats="languages">
            <SideSection title="Languages" />
            <p style={{ fontSize: fs(DS.fs.small), color: 'rgba(255,255,255,0.82)', margin: 0 }}>{languageList.join(' · ')}</p>
          </section>
        )}
      </aside>

      {/* ─── Right Main Content ─── */}
      <main style={{ flex: 1, padding: `${DS.sp.pageY} 20px`, boxSizing: 'border-box' }}>

        {/* Summary */}
        {(summaryText || editMode) && (
          <section data-ats="summary" style={{ marginBottom: DS.sp.sectionMb }}>
            <MainSection title="Profile" />
            <EditableField editMode={editMode} value={summaryText} onSave={v => onFieldUpdate('summary', v)}
              tag="p" multiline style={{ fontSize: fs(DS.fs.body), color: '#374151', lineHeight: 1.65, margin: 0 }} placeholder="Creative professional who..." />
          </section>
        )}

        {/* Experience */}
        {experience.length > 0 && (
          <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
            <MainSection title="Experience" />
            {experience.map((exp, idx) => (
              <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`experience.${idx}.role`, v)}
                      tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 800, color: '#0f172a', margin: 0 }} />
                    {exp.company && <span style={{ fontSize: fs(DS.fs.body), color: accentColor, fontWeight: 600 }}>{exp.company}</span>}
                  </div>
                  <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`experience.${idx}.duration`, v)}
                    tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic', whiteSpace: 'nowrap', marginLeft: 12 }} />
                </div>
                <BulletList points={exp.points} accentColor={accentColor} />
              </div>
            ))}
          </section>
        )}

        {/* Internships */}
        {internships.length > 0 && (
          <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
            <MainSection title="Internships" />
            {internships.map((exp, idx) => (
              <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`internships.${idx}.role`, v)}
                      tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 800, color: '#0f172a', margin: 0 }} />
                    {exp.company && <span style={{ fontSize: fs(DS.fs.body), color: accentColor, fontWeight: 600 }}>{exp.company}</span>}
                  </div>
                  <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`internships.${idx}.duration`, v)}
                    tag="span" style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic', whiteSpace: 'nowrap', marginLeft: 12 }} />
                </div>
                <BulletList points={exp.points} accentColor={accentColor} />
              </div>
            ))}
          </section>
        )}

        {/* Projects / Portfolio */}
        {projects.length > 0 && (
          <section data-ats="projects" style={{ marginBottom: DS.sp.sectionMb }}>
            <MainSection title="Portfolio / Projects" />
            {projects.map((proj, idx) => (
              <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div>
                    <span style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a' }}>{proj.title || proj.name}</span>
                    {proj.tech?.length > 0 && (
                      <span style={{ fontSize: fs(DS.fs.small), color: accentColor, marginLeft: 8, fontStyle: 'italic' }}>
                        [{Array.isArray(proj.tech) ? proj.tech.join(', ') : proj.tech}]
                      </span>
                    )}
                  </div>
                  {proj.impact && <span style={{ fontSize: fs(DS.fs.small), color: '#059669', fontWeight: 600 }}>{proj.impact}</span>}
                </div>
                <BulletList points={proj.points} accentColor={accentColor} />
              </div>
            ))}
          </section>
        )}

        {achieveList.length > 0 && (
          <section data-ats="achievements" style={{ marginBottom: DS.sp.sectionMb }}>
            <MainSection title="Achievements" />
            <BulletList points={achieveList} accentColor={accentColor} />
          </section>
        )}
        {extraList.length > 0 && (
          <section data-ats="extracurricular" style={{ marginBottom: DS.sp.sectionMb }}>
            <MainSection title="Activities" />
            <BulletList points={extraList} accentColor={accentColor} />
          </section>
        )}
      </main>
    </div>
  );
}

export default CreativeProfessionalTemplate;
